import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import { Characteristics } from "../../../model/Characteristics";
import { EditFighterCharacteristicsDto, Fighter } from "../../../model/Dto/Fighter";
import { Api } from "../../../request/api/api";

interface EditCharacteristicsDialogProps {
  open: boolean;
  onClose: () => void;
  fetchData: () => void;
  fighter: Fighter;
}

const CHARACTERISTIC_LABELS: Record<keyof Characteristics, string> = {
    m: "M",
    ws: "WS",
    bs: "BS",
    s: "S",
    t: "T",
    w: "W",
    i: "I",
    a: "A",
    ld: "Ld",
    cl: "Cl",
    wp: "Wp",
    int: "Int",
};

const keys = Object.keys(CHARACTERISTIC_LABELS) as (keyof Characteristics)[];

const charsToStrings = (chars: Characteristics): Record<keyof Characteristics, string> => {
    const result = {} as Record<keyof Characteristics, string>;
    keys.forEach(k => { result[k] = String(chars[k]); });
    return result;
};

const parseInputs = (inputs: Record<keyof Characteristics, string>): Characteristics => {
    const result = new Characteristics();
    keys.forEach(k => { result[k] = parseInt(inputs[k]) || 0; });
    return result;
};

export default function EditCharacteristicsDialog({
  open,
  onClose,
  fetchData,
  fighter,
}: EditCharacteristicsDialogProps) {
  const [characteristicInputs, setCharacteristicInputs] = useState<Record<keyof Characteristics, string>>(
      charsToStrings(new Characteristics())
  );
  const [ratingModificatorInput, setRatingModificatorInput] = useState('0');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open && fighter) {
      const chars = new Characteristics(fighter.userCharacteristicsDtoModifiers || {});
      setCharacteristicInputs(charsToStrings(chars));
      setRatingModificatorInput(String(fighter.userRatingModificator || 0));
      setError(null);
      setSuccess(false);
    }
  }, [open, fighter]);

  const handleCharacteristicChange = (key: keyof Characteristics, value: string) => {
    setCharacteristicInputs(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const data: EditFighterCharacteristicsDto = {
        userCharacteristicModifications: parseInputs(characteristicInputs),
        userRatingModificator: parseInt(ratingModificatorInput) || 0,
      };

      const result = await Api.fighter.editCharacteristics(fighter.id, data);

      if (result) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
          fetchData();
        }, 1000);
      } else {
        setError("Ошибка при сохранении изменений");
      }
    } catch (err: any) {
      setError(err.message || "Произошла ошибка при сохранении");
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = () => {
    const initialCharacteristics = new Characteristics(fighter.userCharacteristicsDtoModifiers || {});
    const initialRating = fighter.userRatingModificator || 0;

    const characteristicsChanged = keys.some(
      (key) => (parseInt(characteristicInputs[key]) || 0) !== initialCharacteristics[key]
    );

    const ratingChanged = (parseInt(ratingModificatorInput) || 0) !== initialRating;

    return characteristicsChanged || ratingChanged;
  };

  const handleClose = () => {
    setError(null);
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">
          Редактирование характеристик бойца
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box mt={2}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Изменения успешно сохранены!
            </Alert>
          )}

          <Typography variant="subtitle1" gutterBottom fontWeight="bold" sx={{ mb: 2 }}>
            Модификаторы характеристик:
          </Typography>

          <Grid container spacing={2}>
            {keys.map((key) => (
              <Grid item xs={6} sm={4} md={3} key={key}>
                <TextField
                  label={CHARACTERISTIC_LABELS[key]}
                  value={characteristicInputs[key]}
                  onChange={(e) => handleCharacteristicChange(key, e.target.value)}
                  fullWidth
                  size="small"
                  disabled={loading}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ inputMode: 'numeric' }}
                />
              </Grid>
            ))}
          </Grid>

          <Box mt={3}>
            <TextField
              label="Модификатор рейтинга"
              value={ratingModificatorInput}
              onChange={(e) => setRatingModificatorInput(e.target.value)}
              fullWidth
              size="medium"
              disabled={loading}
              InputLabelProps={{ shrink: true }}
              inputProps={{ inputMode: 'numeric' }}
              helperText="Прямое изменение рейтинга бойца"
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
        >
          Отмена
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading || !hasChanges()}
        >
          {loading ? "Сохранение..." : "Сохранить"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
